/**
 * Connect Endpoints — the enrollment mission, composed. The three
 * ingredients that otherwise live on separate admin pages (mint a
 * provisioning token, download an installer, run it with the token) sit
 * side by side above a live strip that listens for the first check-in and
 * then the first AI activity.
 *
 * Mounted two ways:
 *   - as the registered `connect` settings view (AdminPage passes viewDef);
 *   - `embedded` as the dashboard-mode front door when the tenant has zero
 *     endpoints (see the connect gate in App.jsx). Same body, no
 *     AdminViewHeader — the dashboard tab strip already sits above.
 *
 * Every section is permission-adaptive rather than permission-gated: a
 * reader who can't mint still sees the mission and a paste field; a role
 * that can't list installers is told to ask for the file. The page is never
 * blank.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { restGet, restPost } from "../lib/restClient.js";
import { query } from "../lib/dataClient.js";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import PageHero from "../components/PageHero.jsx";
import { useEndpointViewTabs } from "../components/EndpointViewTabs.jsx";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { denialOrRawMessage } from "../lib/denialReason.js";
import {
    ARCH_LABEL,
    FORMAT_LABEL,
    PLATFORM_LABEL,
    collapseUniversalRows,
    flattenInstaller,
    latestPerEnvironment,
} from "./InstallersPage.jsx";
import { markTenantConnected, useConnectState } from "../lib/connectState.js";
import {
    detectInstallPlatform,
    installCommandFor,
    installerPlatforms,
    looksLikeJwt,
    pickInstaller,
} from "../lib/connectCommands.js";
import { flattenInventoryRow } from "../lib/inventoryRows.js";
import { NOTE_CLASS } from "../lib/ui.js";
import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";
import { STATIC_MDM_PROFILES, downloadTextFile } from "../lib/mdmProfiles.js";

const CHECKIN_POLL_MS = 5000;
// Once the fleet exists the strip is a watch, not a wait — poll gently.
const CHECKIN_POLL_SLOW_MS = 15000;
const ACTIVITY_POLL_MS = 8000;
const RECENT_TOTAL_REFRESH_MS = 60000;
const RECENT_LIMIT = 25;

// Mint defaults mirror ProvisioningTokensPage (7 days / 10 uses). The custom
// option below keeps the quick path compact while allowing less common limits.
const DEFAULT_MINT_DURATION_DAYS = 7;
const DEFAULT_MINT_MAX_USES = 10;
const MAX_MINT_DURATION_DAYS = Math.floor(0xffffffff / 24);
const MAX_MINT_USES = 100000;
const MINT_DURATION_OPTIONS = [1, 7, 14, 30, 90, 365];
const MINT_MAX_USES_OPTIONS = [1, 10, 25, 50, 100, 500];
const CUSTOM_MINT_VALUE = "custom";

const INSTALLER_FORMAT_ORDER = [
    "INSTALLER_FORMAT_MSI",
    "INSTALLER_FORMAT_EXE",
    "INSTALLER_FORMAT_PKG",
];

export function installerChoicesForRows(rows) {
    const platforms = installerPlatforms(rows);
    const sortFormats = (formats) => formats.sort((a, b) => {
        const ai = INSTALLER_FORMAT_ORDER.indexOf(a);
        const bi = INSTALLER_FORMAT_ORDER.indexOf(b);
        return (ai === -1 ? INSTALLER_FORMAT_ORDER.length : ai) - (bi === -1 ? INSTALLER_FORMAT_ORDER.length : bi)
            || a.localeCompare(b);
    });
    const platformLabels = {
        PLATFORM_WINDOWS: "Windows",
        PLATFORM_MACOS: "macOS",
        PLATFORM_LINUX: "Linux",
    };
    return platforms.flatMap((platform) => sortFormats([...new Set(rows
        .filter((row) => row.os === platform)
        .map((row) => row.installerFormat)
        .filter(Boolean))])
        .map((format) => ({
            key: platform + "-" + format,
            label: (platformLabels[platform] ?? platform) + " " + format.replace("INSTALLER_FORMAT_", "") + " Installer",
            platform,
            format,
        })));
}

// The minted/pasted JWT is held for the SESSION, in module scope like the
// auth token store — never persisted. Navigating away and back keeps the
// command block populated without re-minting (the gateway only returns the
// JWT once).
let _sessionJwt = null;

function CopyButton({ text, disabled = false }) {
    const [copied, setCopied] = useState(false);
    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    }, [text]);
    return (
        <button
            type="button"
            onClick={copy}
            disabled={disabled}
            className="h-[26px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 cursor-pointer"
        >
            {copied ? "Copied" : "Copy"}
        </button>
    );
}

function ShimmerBar() {
    return <div aria-hidden="true" className="connect-shimmer h-[3px] w-full rounded-[1px]" />;
}

function StepCell({ n, title, children }) {
    return (
        <div className="grid content-start gap-[8px] border-l border-[color:var(--line-soft)] px-[14px] py-[12px] first:border-l-0 max-[901px]:border-l-0 max-[901px]:border-t max-[901px]:border-[color:var(--line-soft)] max-[901px]:first:border-t-0">
            <div className="flex items-baseline gap-[7px]">
                <span className="font-mono text-[10px] leading-[1.1] text-[color:var(--muted-soft)]">0{n}</span>
                <strong className="text-[13px] leading-[1.15] font-medium">{title}</strong>
            </div>
            {children}
        </div>
    );
}

function positiveInteger(value, maximum = Number.MAX_SAFE_INTEGER) {
    const normalized = String(value ?? "").trim();
    if (!/^\d+$/.test(normalized)) return null;
    const parsed = Number(normalized);
    return Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= maximum ? parsed : null;
}

function positiveIntegerInput(value) {
    return String(value ?? "").replace(/\D/g, "").replace(/^0+/, "");
}

// ── Step 1: token ────────────────────────────────────────────────

export function TokenStep({ jwt, onJwt, canManage, caps }) {
    const [minting, setMinting] = useState(false);
    const [mintError, setMintError] = useState(null);
    const [paste, setPaste] = useState("");
    const [optionsOpen, setOptionsOpen] = useState(false);
    const [durationDays, setDurationDays] = useState(String(DEFAULT_MINT_DURATION_DAYS));
    const [customDurationDays, setCustomDurationDays] = useState("");
    const [maxUses, setMaxUses] = useState(String(DEFAULT_MINT_MAX_USES));
    const [customMaxUses, setCustomMaxUses] = useState("");

    const mint = useCallback(async () => {
        const selectedDurationDays = positiveInteger(
            durationDays === CUSTOM_MINT_VALUE ? customDurationDays : durationDays,
            MAX_MINT_DURATION_DAYS,
        );
        const selectedMaxUses = positiveInteger(
            maxUses === CUSTOM_MINT_VALUE ? customMaxUses : maxUses,
            MAX_MINT_USES,
        );
        if (selectedDurationDays === null) {
            setMintError("Enter a whole number of days greater than zero.");
            return;
        }
        if (selectedMaxUses === null) {
            setMintError("Enter a whole number of uses greater than zero.");
            return;
        }

        setMinting(true);
        setMintError(null);
        try {
            const resp = await restPost("/v1/provisioning-tokens", {
                duration_hours: selectedDurationDays * 24,
                max_uses: selectedMaxUses,
            });
            const token = resp?.jwt ?? "";
            if (!token) throw new Error("The gateway returned no token.");
            onJwt(token);
        } catch (err) {
            setMintError(denialOrRawMessage(err, caps));
        } finally {
            setMinting(false);
        }
    }, [caps, customDurationDays, customMaxUses, durationDays, maxUses, onJwt]);

    if (jwt) {
        return (
            <>
                <div className="grid items-stretch gap-[6px] [grid-template-columns:minmax(0,1fr)_auto]">
                    <pre className="m-0 truncate rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[8px] py-[5px] font-mono text-[10px] leading-[1.6] text-[color:var(--ink)]">
                        {jwt}
                    </pre>
                    <CopyButton text={jwt} />
                </div>
                <span className={NOTE_CLASS}>
                    Held for this session only. It's already spliced into the command in step 3.
                </span>
            </>
        );
    }

    if (canManage) {
        return (
            <>
                <button
                    type="button"
                    onClick={mint}
                    disabled={minting}
                    className="h-[30px] w-max rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase font-medium text-[color:var(--ink)] disabled:opacity-50 cursor-pointer"
                >
                    {minting ? "Generating…" : "Generate token"}
                </button>
                <span className={NOTE_CLASS}>
                    Valid {durationDays === CUSTOM_MINT_VALUE
                        ? (customDurationDays ? `${customDurationDays} days` : "a custom duration")
                        : `${durationDays} ${durationDays === "1" ? "day" : "days"}`}, registers up to {maxUses === CUSTOM_MINT_VALUE
                        ? (customMaxUses || "a custom limit")
                        : maxUses} endpoints. Shown once.
                </span>
                <button
                    type="button"
                    aria-expanded={optionsOpen}
                    onClick={() => setOptionsOpen((open) => !open)}
                    className="w-max bg-transparent p-0 text-[11px] leading-[1.3] text-[color:var(--muted-soft)] underline decoration-[color:var(--line-strong)] underline-offset-2 transition-colors hover:text-[color:var(--ink)] cursor-pointer"
                >
                    {optionsOpen ? "Hide options" : "Need more options?"}
                </button>
                {optionsOpen ? (
                    <div className="grid items-start gap-[6px] [grid-template-columns:minmax(0,1fr)_minmax(0,1fr)]">
                        <label className="grid gap-[3px]">
                            <span className={`${NOTE_CLASS} text-[10px]`}>Duration (days)</span>
                            <select
                                aria-label="Duration (days)"
                                value={durationDays}
                                onChange={(e) => setDurationDays(e.target.value)}
                                className="min-h-[28px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[7px] text-[11px] text-[color:var(--ink)] outline-none"
                            >
                                {MINT_DURATION_OPTIONS.map((days) => <option key={days} value={days}>{days}</option>)}
                                <option value={CUSTOM_MINT_VALUE}>Custom</option>
                            </select>
                            {durationDays === CUSTOM_MINT_VALUE ? (
                                <input
                                    type="number"
                                    min="1"
                                    max={MAX_MINT_DURATION_DAYS}
                                    step="1"
                                    inputMode="numeric"
                                    aria-label="Custom duration (days)"
                                    placeholder="Enter days"
                                    value={customDurationDays}
                                    onChange={(e) => setCustomDurationDays(positiveIntegerInput(e.target.value))}
                                    onKeyDown={(e) => {
                                        if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
                                    }}
                                    className="min-h-[28px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[7px] text-[11px] text-[color:var(--ink)] outline-none"
                                />
                            ) : null}
                        </label>
                        <label className="grid gap-[3px]">
                            <span className={`${NOTE_CLASS} text-[10px]`}>Max uses</span>
                            <select
                                aria-label="Max uses"
                                value={maxUses}
                                onChange={(e) => setMaxUses(e.target.value)}
                                className="min-h-[28px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[7px] text-[11px] text-[color:var(--ink)] outline-none"
                            >
                                {MINT_MAX_USES_OPTIONS.map((uses) => <option key={uses} value={uses}>{uses}</option>)}
                                <option value={CUSTOM_MINT_VALUE}>Custom</option>
                            </select>
                            {maxUses === CUSTOM_MINT_VALUE ? (
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    inputMode="numeric"
                                    aria-label="Custom max uses"
                                    max={MAX_MINT_USES}
                                    placeholder="Enter uses"
                                    value={customMaxUses}
                                    onChange={(e) => setCustomMaxUses(positiveIntegerInput(e.target.value))}
                                    onKeyDown={(e) => {
                                        if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
                                    }}
                                    className="min-h-[28px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[7px] text-[11px] text-[color:var(--ink)] outline-none"
                                />
                            ) : null}
                        </label>
                    </div>
                ) : null}
                {mintError ? <span className="text-[11px] leading-[1.3] text-red-400">{mintError}</span> : null}
            </>
        );
    }

    const pasteValid = looksLikeJwt(paste);
    return (
        <>
            <span className={NOTE_CLASS}>
                Minting needs the provisioning-tokens manage role. Ask a tenant admin for a token, then paste it here:
            </span>
            <div className="grid items-stretch gap-[6px] [grid-template-columns:minmax(0,1fr)_auto]">
                <input
                    type="text"
                    value={paste}
                    onChange={(e) => setPaste(e.target.value)}
                    placeholder="eyJ… provisioning token"
                    spellCheck={false}
                    className="min-h-[28px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] font-mono text-[10px] leading-[1.4] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                />
                <button
                    type="button"
                    onClick={() => pasteValid && onJwt(paste.trim())}
                    disabled={!pasteValid}
                    className="h-[28px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer"
                >
                    Use
                </button>
            </div>
        </>
    );
}

// ── Step 2: installer ────────────────────────────────────────────

function formatBytesShort(n) {
    if (!Number.isFinite(n) || n <= 0) return null;
    const units = ["B", "KB", "MB", "GB"];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function InstallerStep({ installers, choiceKey, onChoice, choices, chosen }) {
    const [dlState, setDlState] = useState(null); // null | "pending" | error string

    const download = useCallback(async () => {
        if (!chosen) return;
        setDlState("pending");
        try {
            const resp = await restPost("/v1/installers:download-url", {
                version: chosen.version,
                os: chosen.os,
                architecture: chosen.architecture,
                installer_format: chosen.installerFormat,
            });
            const url = resp?.downloadUrl ?? resp?.download_url ?? "";
            if (!url) throw new Error("Empty download_url in response");
            window.open(url, "_blank", "noopener,noreferrer");
            setDlState(null);
        } catch (err) {
            setDlState(err.message || String(err));
        }
    }, [chosen]);

    if (installers.status === "loading") {
        return <div aria-hidden="true" className="h-[30px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 }} />;
    }
    if (installers.status === "denied") {
        return (
            <span className={NOTE_CLASS}>
                Your role can't list installers. Ask an admin to send you the installer for this machine; the command in step 3 still applies.
            </span>
        );
    }
    if (installers.status === "error") {
        return <span className="text-[11px] leading-[1.3] text-red-400">Couldn't load the installer catalog. Reload to retry.</span>;
    }
    if (!installers.rows.length) {
        return <span className={NOTE_CLASS}>No installer artifacts published yet.</span>;
    }

    return (
        <>
            <label className="grid gap-[4px]">
                <span className={NOTE_CLASS}>Select an installer</span>
                <select
                    aria-label="Choose an installer"
                    value={choiceKey || ""}
                    onChange={(e) => onChoice(e.target.value || null)}
                    className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] text-[11px] text-[color:var(--ink)] outline-none"
                >
                    <option value="">Please select an installer</option>
                    {choices.map((choice) => (
                        <option key={choice.key} value={choice.key} disabled={!choice.installer}>
                            {choice.label}{choice.installer ? ` - v${choice.installer.version}` : " — unavailable"}
                        </option>
                    ))}
                </select>
            </label>
            {chosen ? (
                <>
                    <div className="flex flex-wrap items-baseline gap-x-[8px] gap-y-[2px]">
                        <span className="text-[13px] leading-[1.1]">Version {chosen.version}</span>
                        <span className={NOTE_CLASS}>
                            {FORMAT_LABEL[chosen.installerFormat] ?? chosen.installerFormat}
                            {" · "}
                            {chosen.isUniversal ? "Universal" : (ARCH_LABEL[chosen.architecture] ?? chosen.architecture)}
                            {formatBytesShort(chosen.sizeBytes) ? ` · ${formatBytesShort(chosen.sizeBytes)}` : ""}
                        </span>
                    </div>
                    {dlState === "pending" ? (
                        <span className={`${NOTE_CLASS} text-[10px]`}>Generating link…</span>
                    ) : (
                        <button
                            type="button"
                            onClick={download}
                            className="h-[30px] w-max rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer"
                        >
                            Download
                        </button>
                    )}
                    {typeof dlState === "string" && dlState !== "pending" ? (
                        <span className="text-[11px] leading-[1.3] text-red-400">{dlState}</span>
                    ) : null}
                    {chosen.downloadSha256 ? (
                        <span className={`${NOTE_CLASS} truncate font-mono text-[9px]`} title={chosen.downloadSha256}>
                            sha256 {chosen.downloadSha256}
                        </span>
                    ) : null}
                </>
            ) : (
                <span className={NOTE_CLASS}>Select an installer to see its version, download details, and command.</span>
            )}
        </>
    );
}

// ── Step 3: run it ───────────────────────────────────────────────

const PLATFORM_RUN_NOTE = {
    "PLATFORM_WINDOWS|INSTALLER_FORMAT_EXE":
        "The bundle elevates itself (one UAC prompt). The same command works under SYSTEM for Intune or SCCM.",
    "PLATFORM_WINDOWS|INSTALLER_FORMAT_MSI":
        "Run from an elevated prompt; msiexec doesn't self-elevate.",
};

// A quiet chip in the MDM disclosure row; the profile downloads as-is.
function ProfileChip({ label, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="h-[24px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[8px] text-[10px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer"
        >
            {label}
        </button>
    );
}

// The macOS MDM path folded behind the run-note line: the note keeps
// carrying the one fact everyone needs (stage the token), and a quiet
// "MDM profiles" toggle expands the pre-approval download for the
// minority who deploy managed. Progressive disclosure, not a fourth step.
function MdmProfilesDisclosure() {
    const [open, setOpen] = useState(false);

    return (
        <div className="grid gap-[6px]">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="w-max bg-transparent p-0 text-[11px] leading-[1.3] text-[color:var(--muted-soft)] underline decoration-[color:var(--line-strong)] underline-offset-2 transition-colors hover:text-[color:var(--ink)] cursor-pointer"
            >
                {open ? "Hide MDM profiles" : "Installing via MDM?"}
            </button>
            {open ? (
                <>
                    <div className="flex flex-wrap items-center gap-[6px]">
                        {STATIC_MDM_PROFILES.map((p) => (
                            <ProfileChip
                                key={p.filename}
                                label={p.label}
                                onClick={() => downloadTextFile(p.filename, p.content)}
                            />
                        ))}
                    </div>
                    <a
                        href="https://support.originhq.com/docs/mdm-deployment-guides"
                        target="_blank"
                        rel="noreferrer"
                        className={`${NOTE_CLASS} w-max underline decoration-[color:var(--line-strong)] underline-offset-2 transition-colors hover:text-[color:var(--ink)]`}
                    >
                        MDM deployment guides ↗
                    </a>
                </>
            ) : null}
        </div>
    );
}

export function CommandStep({ platform, chosen, jwt }) {
    // Keep the command useful when the catalog is unavailable or the role
    // cannot read it. The installer filename remains a deliberate placeholder
    // until an administrator supplies the actual file.
    const detectedPlatform = platform ?? detectInstallPlatform() ?? "PLATFORM_WINDOWS";
    const fallbackFormat = detectedPlatform === "PLATFORM_MACOS"
        ? "INSTALLER_FORMAT_PKG"
        : detectedPlatform === "PLATFORM_WINDOWS"
            ? "INSTALLER_FORMAT_EXE"
            : null;
    if (!chosen && !fallbackFormat) {
        return <span className={NOTE_CLASS}>Launch the installer for this machine and paste the token during installation.</span>;
    }
    const command = installCommandFor({
        platform: chosen ? platform : detectedPlatform,
        format: chosen?.installerFormat ?? fallbackFormat,
        version: chosen?.version,
        architecture: chosen?.architecture,
        jwt,
    });
    if (!command) {
        return (
            <span className={NOTE_CLASS}>
                No packaged install command for this platform. Talk to your Origin contact about deployment.
            </span>
        );
    }
    const note = PLATFORM_RUN_NOTE[
        String(chosen ? platform : detectedPlatform) + "|" + (chosen?.installerFormat ?? fallbackFormat)
    ];
    return (
        <>
            <div className="flex items-center justify-between gap-[8px]">
                <span className={NOTE_CLASS}>
                    {jwt ? "Token included. Run it on the endpoint." : "Substitute the token from step 1."}
                </span>
                <CopyButton text={command} />
            </div>
            <pre className="m-0 overflow-x-auto whitespace-pre-wrap break-words rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[9px] py-[7px] font-mono text-[10px] leading-[1.6] text-[color:var(--ink)]">
                {command}
            </pre>
            {note ? <span className={NOTE_CLASS}>{note}</span> : null}
            {(chosen ? platform : detectedPlatform) === "PLATFORM_MACOS" ? (
                <>
                    <span className={NOTE_CLASS}>
                        The command stages the token for the PKG installer, which does not inherit shell environment variables.
                    </span>
                    <MdmProfilesDisclosure />
                </>
            ) : null}
        </>
    );
}

// ── The live strip ───────────────────────────────────────────────

function endpointSeenLabel(d) {
    if (!(d instanceof Date) || !Number.isFinite(d.getTime())) return "—";
    return `${timeLabelFormatter.format(d)} · ${dayLabelFormatter.format(d)}`;
}

export function endpointRowKey(row) {
    return row?.idRaw || row?.hostname || "";
}

/**
 * Merge a fresh first page into the live strip without dropping pages the
 * user already loaded. A fixed row slice is incorrect when an endpoint moves
 * between pages after a heartbeat refresh; only rows fetched by an explicit
 * "Load more" action are pinned across refreshes.
 */
export function mergeRecentEndpointPage(current, freshRows, total, nextCursor = null) {
    const freshKeys = new Set(freshRows.map(endpointRowKey));
    const pinnedKeys = current?.pinnedKeys ?? new Set();
    const preserved = (current?.rows ?? []).filter((row) => {
        const key = endpointRowKey(row);
        return pinnedKeys.has(key) && !freshKeys.has(key);
    }).slice(0, Math.max(Number(total) - freshRows.length, 0));
    return {
        rows: [...freshRows, ...preserved],
        total,
        // A refresh establishes a new page boundary. Keeping the old cursor
        // would page against a ranking that no longer exists; explicit null
        // must remain null after the end of the list.
        nextCursor,
        pinnedKeys,
    };
}

export function appendRecentEndpointPage(current, pageRows, total, nextCursor) {
    const existingKeys = new Set((current?.rows ?? []).map(endpointRowKey));
    const additions = pageRows.filter((row) => !existingKeys.has(endpointRowKey(row)));
    const pinnedKeys = new Set(current?.pinnedKeys ?? []);
    for (const row of additions) pinnedKeys.add(endpointRowKey(row));
    return {
        rows: [...(current?.rows ?? []), ...additions],
        total: Number(total ?? current?.total ?? 0),
        nextCursor: nextCursor ?? null,
        pinnedKeys,
    };
}

function CheckMark() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-[14px] w-[14px] text-[color:var(--green)]">
            <path d="M3 8.5 6.5 12 13 4.5" />
        </svg>
    );
}

export function LiveStrip({ canReadEndpoints, canAnalytics, startedEmpty, onOpenExplore }) {
    // null = first fetch pending; { rows, total } thereafter.
    const [fleet, setFleet] = useState(null);
    const [fleetDenied, setFleetDenied] = useState(false);
    const [fleetError, setFleetError] = useState(false);
    const [activitySeen, setActivitySeen] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const hasEndpoints = (fleet?.total ?? 0) > 0;
    const canWatchFleet = canAnalytics || canReadEndpoints;
    const recentTotalRef = useRef(null);
    const lastTotalRefreshRef = useRef(0);

    // No visibility gating on the interval — the browser already throttles
    // hidden-tab timers, and skipping ticks outright left the strip stuck
    // on its initial state for a user who installed the agent with the
    // dashboard in a background window. The visibilitychange tick just
    // freshens the strip the moment they come back.
    const loadMoreGeneration = useRef(0);
    useEffect(() => () => { loadMoreGeneration.current += 1; }, []);

    useEffect(() => {
        if (!canWatchFleet || fleetDenied) return undefined;
        let cancelled = false;
        const tick = async () => {
            try {
                const includeTotal = canAnalytics && (
                    recentTotalRef.current === null
                    || Date.now() - lastTotalRefreshRef.current >= RECENT_TOTAL_REFRESH_MS
                );
                const resp = canAnalytics
                    ? await query(
                        "getRecentEndpointPage",
                        { limit: RECENT_LIMIT, includeTotal },
                        { cache: false },
                    )
                    : await restGet("/v1/endpoints/metadata", { page: 0, page_size: RECENT_LIMIT });
                if (cancelled) return;
                const rows = canAnalytics
                    ? resp?.rows ?? []
                    : (resp?.metadata ?? []).map(flattenInventoryRow);
                const fetchedTotal = resp?.total == null ? null : Number(resp.total);
                if (fetchedTotal !== null) {
                    recentTotalRef.current = fetchedTotal;
                    lastTotalRefreshRef.current = Date.now();
                }
                const total = fetchedTotal ?? Math.max(recentTotalRef.current ?? 0, rows.length);
                setFleet((current) => mergeRecentEndpointPage(
                    current,
                    rows,
                    total,
                    canAnalytics ? resp?.nextCursor ?? null : null,
                ));
                setFleetError(false);
                if (total > 0) markTenantConnected(total);
            } catch (err) {
                if (cancelled) return;
                // A denial is terminal (the role can't read the fleet); a
                // network blip keeps the interval so the strip self-heals.
                if (err?.status === 403) setFleetDenied(true);
                else setFleetError(true);
            }
        };
        tick();
        const id = window.setInterval(tick, hasEndpoints ? CHECKIN_POLL_SLOW_MS : CHECKIN_POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible") tick();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            cancelled = true;
            window.clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [canAnalytics, canReadEndpoints, canWatchFleet, fleetDenied, hasEndpoints]);

    const loadMore = useCallback(async () => {
        if (!fleet || loadingMore || fleet.rows.length >= fleet.total || !fleet.nextCursor) return;
        setLoadingMore(true);
        const generation = loadMoreGeneration.current;
        try {
            const resp = await query(
                "getRecentEndpointPage",
                { cursor: fleet.nextCursor, limit: RECENT_LIMIT, includeTotal: false },
                { cache: false },
            );
            if (generation !== loadMoreGeneration.current) return;
            setFleet((current) => appendRecentEndpointPage(
                current,
                resp?.rows ?? [],
                resp?.total,
                resp?.nextCursor,
            ));
        } catch {
            if (generation === loadMoreGeneration.current) setFleetError(true);
        } finally {
            if (generation === loadMoreGeneration.current) setLoadingMore(false);
        }
    }, [fleet, loadingMore]);

    // Second phase of the arc, only for a session that STARTED empty: probe
    // for the first prompt-bearing endpoint via the analytics graph (the
    // same population the Explore sidebar counts). A tenant that was already
    // connected skips it — "first activity" means nothing to them.
    useEffect(() => {
        if (!startedEmpty || !canAnalytics || !hasEndpoints || activitySeen) return undefined;
        let cancelled = false;
        const tick = async () => {
            try {
                const resp = await query(
                    "getDimensionEntries",
                    { dimKey: "endpoints", parentScopePath: [], limit: 1, sparkline: false, minimal: true },
                    { cache: false },
                );
                if (cancelled) return;
                if ((resp?.total ?? 0) > 0) setActivitySeen(true);
            } catch {
                // Best-effort probe; the next tick retries.
            }
        };
        tick();
        const id = window.setInterval(tick, ACTIVITY_POLL_MS);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [startedEmpty, canAnalytics, hasEndpoints, activitySeen]);

    if (!canWatchFleet) {
        return (
            <section className="grid gap-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[14px] py-[12px]">
                <strong className="text-[13px] leading-[1.15] font-medium">Check-ins land in Endpoint Inventory</strong>
                <span className={NOTE_CLASS}>Ask a tenant admin to confirm that the endpoint arrived.</span>
            </section>
        );
    }
    if (fleetDenied) {
        return (
            <section className="grid gap-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[14px] py-[12px]">
                <strong className="text-[13px] leading-[1.15] font-medium">Check-ins land in Endpoint Inventory</strong>
                <span className={NOTE_CLASS}>
                    Your role can't query endpoint activity. Ask an admin to confirm the endpoint arrived.
                </span>
            </section>
        );
    }

    return (
        <section
            aria-busy={fleet === null ? "true" : undefined}
            aria-live="polite"
            className="grid gap-[8px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[14px] py-[12px]"
        >
            {fleet === null ? (
                <>
                    <strong className="text-[13px] leading-[1.15] font-medium">Checking the fleet…</strong>
                    <ShimmerBar />
                    {fleetError ? (
                        <span className={NOTE_CLASS}>Connection hiccup. Retrying.</span>
                    ) : null}
                </>
            ) : !hasEndpoints ? (
                <>
                    <strong className="text-[13px] leading-[1.15] font-medium">Listening for the first check-in</strong>
                    <ShimmerBar />
                    <span className={NOTE_CLASS}>
                        Endpoints register and report within about a minute of the installer finishing. This page updates itself.
                        {fleetError ? " Connection hiccup. Retrying." : ""}
                    </span>
                </>
            ) : (
                <>
                    <div className="flex items-center gap-[8px]">
                        <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-[color:var(--green)]" />
                        <strong className="text-[13px] leading-[1.15] font-medium">
                            {canAnalytics
                                ? (fleet.total === 1 ? "1 endpoint reporting in the last 30 days" : `${fleet.total} endpoints reporting in the last 30 days`)
                                : (fleet.total === 1 ? "1 endpoint connected" : `${fleet.total} endpoints connected`)}
                        </strong>
                    </div>
                    <div className="grid">
                        {fleet.rows.map((r) => (
                            <div
                                key={r.idRaw || r.hostname}
                                className="page-enter grid items-center gap-x-[10px] border-t border-[color:var(--line-soft)] py-[5px] text-[11px] leading-[1.15] [grid-template-columns:minmax(120px,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_auto]"
                            >
                                <strong className="truncate font-medium">{r.hostname || "—"}</strong>
                                <span className="truncate text-[color:var(--muted)]">{r.platform || "—"}</span>
                                <span className="truncate text-[color:var(--muted)]">
                                    {r.agentVersion ? `agent ${r.agentVersion}` : "—"}
                                </span>
                                <span className="text-[color:var(--muted-soft)]">{endpointSeenLabel(r.snapshotTime)}</span>
                            </div>
                        ))}
                    </div>
                    {canAnalytics && fleet.nextCursor && fleet.rows.length < fleet.total ? (
                        <div className="flex flex-wrap items-center justify-between gap-[8px] border-t border-[color:var(--line-soft)] pt-[8px]">
                            <span className={NOTE_CLASS}>Showing {fleet.rows.length} of {fleet.total} recent endpoints.</span>
                            <button
                                type="button"
                                onClick={loadMore}
                                disabled={loadingMore}
                                className="h-[28px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[9px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer"
                            >
                                {loadingMore ? "Loading…" : "Load more endpoints"}
                            </button>
                        </div>
                    ) : null}
                    {startedEmpty && canAnalytics && !activitySeen ? (
                        <div className="grid gap-[6px] border-t border-[color:var(--line-soft)] pt-[8px]">
                            <strong className="text-[12px] leading-[1.15] font-medium">Waiting for the first AI activity</strong>
                            <ShimmerBar />
                            <span className={NOTE_CLASS}>
                                Use an AI tool on that machine: an editor copilot, a chat app, a CLI agent. Its first prompt lands here.
                            </span>
                        </div>
                    ) : null}
                    {startedEmpty && activitySeen ? (
                        <div className="page-enter flex flex-wrap items-center justify-between gap-[10px] border-t border-[color:var(--line-soft)] pt-[8px]">
                            <div className="flex items-center gap-[8px]">
                                <CheckMark />
                                <div className="grid gap-[1px]">
                                    <strong className="text-[13px] leading-[1.15] font-medium">First AI activity captured</strong>
                                    <span className={NOTE_CLASS}>Your org's AI usage is on the record from here on.</span>
                                </div>
                            </div>
                            {onOpenExplore ? (
                                <button
                                    type="button"
                                    onClick={onOpenExplore}
                                    className="h-[30px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[12px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase font-medium text-[color:var(--ink)] cursor-pointer"
                                >
                                    Open Explore
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                </>
            )}
        </section>
    );
}

// ── Page ─────────────────────────────────────────────────────────

function RoutedConnectHeader({ title, canReadTokens, canInstallers }) {
    const tabs = useEndpointViewTabs({ active: "connect", canReadTokens, canInstallers });
    return <AdminViewHeader title={title} scope="All endpoints" tabs={tabs} />;
}

export default function ConnectPage({ viewDef, embedded = false, onSkip = null, onScopeNavigate = null }) {
    const caps = useMyPermissions();
    const canManage = hasPermission(caps, PERMISSIONS.PROVISIONING_TOKENS_MANAGE);
    const canReadTokens = hasPermission(caps, PERMISSIONS.PROVISIONING_TOKENS_READ);
    const canInstallers = hasPermission(caps, PERMISSIONS.INSTALLERS_READ);
    const canReadEndpoints = hasPermission(caps, PERMISSIONS.ENDPOINT_METADATA_READ);
    const canAnalytics = hasPermission(caps, PERMISSIONS.ANALYTICS_EXECUTE);

    const connect = useConnectState();
    // "Started empty" pins the arc: the completion treatment only belongs to
    // the session that watched its first endpoint arrive. Latched from the
    // first confirmed reading so the strip doesn't lose its arc the moment
    // the endpoint checks in and `connected` flips.
    const startedEmptyRef = useRef(null);
    if (startedEmptyRef.current === null && connect.status === "ready") {
        startedEmptyRef.current = connect.connected === false;
    }
    const startedEmpty = startedEmptyRef.current === true;

    const [jwt, setJwt] = useState(_sessionJwt);
    const adoptJwt = useCallback((token) => {
        _sessionJwt = token;
        setJwt(token);
    }, []);

    // Installer catalog — one shot; the artifact list doesn't change under
    // the user mid-mission. { status, rows } with rows pre-reduced to the
    // newest build per environment and universal-collapsed.
    const [installers, setInstallers] = useState({ status: canInstallers ? "loading" : "denied", rows: [] });
    useEffect(() => {
        if (!canInstallers) {
            setInstallers({ status: "denied", rows: [] });
            return undefined;
        }
        let cancelled = false;
        setInstallers({ status: "loading", rows: [] });
        restGet("/v1/installers", { page: 0, page_size: 1000 })
            .then((resp) => {
                if (cancelled) return;
                const flat = (resp?.installers ?? []).map(flattenInstaller);
                const rows = collapseUniversalRows(latestPerEnvironment(flat));
                setInstallers({ status: "ready", rows });
            })
            .catch((err) => {
                if (cancelled) return;
                setInstallers({ status: err?.status === 403 ? "denied" : "error", rows: [] });
            });
        return () => { cancelled = true; };
    }, [canInstallers]);

    const [installerChoice, setInstallerChoice] = useState(null);
    const installerChoices = useMemo(() => installerChoicesForRows(installers.rows).map((choice) => ({
        ...choice,
        installer: pickInstaller(installers.rows, choice.platform, choice.format),
    })), [installers.rows]);
    useEffect(() => {
        if (!installerChoices.length) {
            setInstallerChoice(null);
            return;
        }
        if (installerChoices.some((choice) => choice.key === installerChoice && choice.installer)) return;
        const detected = detectInstallPlatform();
        const preferred = installerChoices.find((choice) => choice.platform === detected && choice.installer)
            ?? installerChoices.find((choice) => choice.installer);
        setInstallerChoice(preferred?.key ?? null);
    }, [installerChoice, installerChoices]);
    const selectedChoice = installerChoices.find((choice) => choice.key === installerChoice) ?? null;
    const platform = selectedChoice?.platform ?? null;
    const chosen = selectedChoice?.installer ?? null;

    const openExplore = onScopeNavigate ? () => onScopeNavigate([]) : null;

    // The embedded mount only exists on the gated front door, so it always
    // speaks the first-run copy even while the connect fetch is unresolved.
    const firstRun = embedded || startedEmpty || connect.connected === false;
    const hero = (
        <PageHero
            eyebrow={firstRun ? "Getting connected" : "Fleet"}
            title={firstRun ? "Bring your first endpoint online" : "Connect endpoints"}
            detail="Origin sees AI usage through its agent on each endpoint. Install the agent on one machine (a laptop, a build box, a VM) and its AI activity shows up here about a minute after first use."
            controls={embedded && onSkip ? (
                <button
                    type="button"
                    onClick={onSkip}
                    className="text-[11px] leading-[1.2] text-[color:var(--muted)] underline-offset-2 transition-colors hover:text-[color:var(--ink)] hover:underline cursor-pointer"
                >
                    Skip for now
                </button>
            ) : null}
        />
    );

    return (
        <>
            {!embedded ? (
                <RoutedConnectHeader
                    title={viewDef?.label ?? "Connect Endpoints"}
                    canReadTokens={canReadTokens}
                    canInstallers={canInstallers}
                />
            ) : null}
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto">
                <div className="grid w-full max-w-none content-start gap-[12px] pb-[24px]">
                    {hero}
                    <div className="grid grid-cols-3 rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] max-[901px]:grid-cols-1">
                        <StepCell n={1} title={canManage ? "Generate a provisioning token" : "Get a provisioning token"}>
                            <TokenStep jwt={jwt} onJwt={adoptJwt} canManage={canManage} caps={caps} />
                        </StepCell>
                        <StepCell n={2} title="Download the installer">
                            <InstallerStep
                                installers={installers}
                                choiceKey={installerChoice}
                                onChoice={setInstallerChoice}
                                choices={installerChoices}
                                chosen={chosen}
                            />
                        </StepCell>
                        <StepCell n={3} title="Install it with the token">
                            <CommandStep platform={platform} chosen={chosen} jwt={jwt} />
                        </StepCell>
                    </div>
                    <LiveStrip
                        canReadEndpoints={canReadEndpoints}
                        canAnalytics={canAnalytics}
                        startedEmpty={startedEmpty}
                        onOpenExplore={openExplore}
                    />
                </div>
            </div>
        </>
    );
}
